import { useCallback, useEffect, useMemo, useState } from 'react'
import { Add, CompareArrows } from '@mui/icons-material'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { green } from '@mui/material/colors'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { Document } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { PDPurchaseOrder, POLineItemMatch } from '@/types/po-match'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import AddPurchaseOrderDialog from './AddPurchaseOrderDialog'
import ConnectorCanvas, { type MatchLineData } from './ConnectorCanvas'
import CreateMatchConfirmDialog from './CreateMatchConfirmDialog'
import DataGroupNode from './DataGroupNode'
import LineItemsTable from './LineItemsTable'
import PurchaseOrderData from './PurchaseOrderData'
import {
  FieldItem,
  getSharedLineItemJob,
  getSharedLineItemPhase,
} from './shared'
import SkeletonPOCard from './SkeletonPOCard'
import UpdateVendorPopover from './UpdateVendorPopover'
import useConnectorPositions from './useConnectorPositions'
import useConnectorSelection from './useConnectorSelection'
import useDocumentForPOMatchingData from './useDocumentForPOMatchingData'
import useInitialPOSort from './useInitialPOSort'
import usePOMatchData, { USER_MATCHED_REASON_ID } from './usePOMatchData'

type PendingMatch = {
  invoiceRowId: string
  poLineItemId: string
  poId: string
}

type PurchaseOrderViewProps = {
  doc: Document
}

export default function PurchaseOrderView({ doc }: PurchaseOrderViewProps) {
  const { project } = useProjectContext()
  const {
    vendorName,
    vendorId,
    vendorField,
    purchaseOrderNumber,
    invoiceDate,
    dueDate,
    amountTotal,
    lineItems,
  } = useDocumentForPOMatchingData()

  const createLineItemMatchOverlay = useOverlay()
  const searchPurchaseOrderOverlay = useOverlay()
  const removePOOverlay = useOverlay()
  const vendorUpdateOverlay = useOverlay()
  const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null)
  const [userAddedPOs, setUserAddedPOs] = useState(
    () => new Map<string, PDPurchaseOrder>(),
  )
  const [pendingMatchLines, setPendingMatchLines] = useState<MatchLineData[]>(
    [],
  )
  const [skipCreateConfirm, setSkipCreateConfirm] = useLocalStorage(
    'po-line-item-match-skip-create-confirm',
    false,
  )
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [poToRemove, setPoToRemove] = useState<{
    externalId: string
    connectionCount: number
  } | null>(null)

  // Data fetching
  const {
    poIds,
    pdPurchaseOrders,
    sortedPurchaseOrders,
    vendorMap,
    reasonMap,
    isLoadingPOs,
    isCreatingLineItemMatch,
    poLineItemMatchPerPoId,
    poLineItemMatchesQueryKey,
    deletePOLineItemMatches,
    createPOLineItemMatch,
  } = usePOMatchData({
    documentId: doc.id,
    ownerOrgId: doc.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    userAddedPOs,
  })

  const suggestedVendor = useMemo(() => {
    const firstPO = sortedPurchaseOrders[0]
    if (!firstPO || !firstPO.vendor_id) return null

    // Only suggest if mismatch
    if (firstPO.vendor_id === vendorId) return null

    return vendorMap.get(firstPO.vendor_id)
  }, [sortedPurchaseOrders, vendorId, vendorMap])

  // Connector position tracking
  const {
    invoiceScrollRef,
    poScrollRef,
    connectorRef,
    invoiceRowPositions,
    poRowPositions,
    invoiceRowRefCallback,
    poRowRefCallback,
    recalcPositions,
  } = useConnectorPositions()

  // Connector selection / highlight state
  const {
    highlightedInvoiceRowIds,
    highlightedPoRowIds,
    handleConnectorSelectionChange,
  } = useConnectorSelection()

  // Recalc positions when data changes
  useEffect(() => {
    const timer = setTimeout(recalcPositions, 0)
    return () => clearTimeout(timer)
  }, [recalcPositions, lineItems, pdPurchaseOrders])

  // Build match line data for ConnectorCanvas
  const matchLines: MatchLineData[] = useMemo(() => {
    const lines: MatchLineData[] = []

    pdPurchaseOrders.forEach((po) => {
      const lineItemMatchesForPO =
        poLineItemMatchPerPoId.get(po.external_id) || []

      for (const lim of lineItemMatchesForPO) {
        const reasonNames = lim.match_reasons_ids
          .map((rid) => reasonMap.get(rid))
          .filter((n): n is string => !!n)

        lines.push({
          id: `match-${lim.id}`,
          sourceRowId: lim.document_row_id,
          targetRowId: `${po.external_id}|${lim.po_line_item_id}`,
          allReasons: reasonNames,
          aiReasoning: lim.ai_reasoning,
          poLineItemMatchId: lim.id,
          onDelete: () => {
            if (lineItemMatchesForPO.length === 1) {
              // Pin the PO in userAddedPOs so it stays visible in the panel.
              // Without this, deleting the last line item match would remove the PO
              // from matchedPoIds, causing it to disappear from the UI entirely.
              // We want to keep it visible so the user can see the PO and re-match to it if desired.
              setUserAddedPOs(
                (prev) => new Map([...prev, [po.external_id, po]]),
              )
            }
          },
        })
      }
    })

    return lines
  }, [pdPurchaseOrders, poLineItemMatchPerPoId, reasonMap])

  // Clear pending lines once server data includes them
  useEffect(() => {
    if (!pendingMatchLines.length) return
    const serverKeys = new Set(
      matchLines.map((m) => `${m.sourceRowId}-${m.targetRowId}`),
    )
    const remaining = pendingMatchLines.filter(
      (p) => !serverKeys.has(`${p.sourceRowId}-${p.targetRowId}`),
    )
    if (remaining.length < pendingMatchLines.length) {
      setPendingMatchLines(remaining)
    }
  }, [matchLines, pendingMatchLines])

  // Merge server matches with pending (in-flight) matches
  const allMatchLines = useMemo(
    () => [...matchLines, ...pendingMatchLines],
    [matchLines, pendingMatchLines],
  )

  const connectedInvoiceRowIds = useMemo(
    () => new Set(allMatchLines.map((m) => m.sourceRowId)),
    [allMatchLines],
  )

  const connectedPoRowIds = useMemo(
    () => new Set(allMatchLines.map((m) => m.targetRowId)),
    [allMatchLines],
  )

  // PO row ID → PO external ID mapping (for drag-to-connect)
  const poRowIdToPoId = useMemo(() => {
    const map = new Map<string, string>()
    for (const po of pdPurchaseOrders) {
      for (const li of po.line_items) {
        map.set(`${po.external_id}|${li.external_id}`, po.external_id)
      }
    }
    return map
  }, [pdPurchaseOrders])

  // Create a line item match directly
  const createLineItemMatch = useCallback(
    async (invoiceRowId: string, poLineItemId: string, poId: string) => {
      if (!doc.id) return

      createPOLineItemMatch({
        id: generateUuid(),
        po_id: poId,
        po_line_item_id: poLineItemId,
        document_row_id: invoiceRowId,
        match_reasons_ids: [USER_MATCHED_REASON_ID],
      } as POLineItemMatch)
    },
    [createPOLineItemMatch, doc.id],
  )

  // Drag-to-connect handler
  const handleDragConnect = useCallback(
    async (invoiceRowId: string, poRowId: string) => {
      const poId = poRowIdToPoId.get(poRowId)
      if (!poId) return
      // Extract raw line item ID from composite "poId|lineItemId" row key
      const rawLineItemId = poRowId.slice(poId.length + 1)

      if (skipCreateConfirm) {
        await createLineItemMatch(invoiceRowId, rawLineItemId, poId)
        return
      }

      setPendingMatch({ invoiceRowId, poLineItemId: rawLineItemId, poId })
      setDoNotShowAgain(false)
      createLineItemMatchOverlay.open()
    },
    [
      createLineItemMatchOverlay,
      createLineItemMatch,
      poRowIdToPoId,
      skipCreateConfirm,
    ],
  )

  const handleCreateConfirmClose = useCallback(() => {
    createLineItemMatchOverlay.close()
    setPendingMatch(null)
    setDoNotShowAgain(false)
  }, [createLineItemMatchOverlay])

  const handleConfirmCreate = useCallback(async () => {
    if (!pendingMatch) return

    if (doNotShowAgain) {
      setSkipCreateConfirm(true)
    }

    await createLineItemMatch(
      pendingMatch.invoiceRowId,
      pendingMatch.poLineItemId,
      pendingMatch.poId,
    )
    handleCreateConfirmClose()
  }, [
    createLineItemMatch,
    doNotShowAgain,
    handleCreateConfirmClose,
    pendingMatch,
    setSkipCreateConfirm,
  ])

  const removeUserAddedPo = useCallback((poId: string) => {
    setUserAddedPOs((prev) => {
      const next = new Map(prev)
      next.delete(poId)
      return next
    })
  }, [])

  const handleAddPurchaseOrder = useCallback(
    (purchaseOrder: PDPurchaseOrder) => {
      setUserAddedPOs((prev) => {
        const next = new Map(prev)
        next.set(purchaseOrder.external_id, purchaseOrder)
        return next
      })
    },
    [],
  )

  const handleRemovePO = useCallback(
    (poExternalId: string) => {
      const lineItemMatches = poLineItemMatchPerPoId.get(poExternalId) ?? []

      if (lineItemMatches.length === 0) {
        // No connections — just remove from local state
        removeUserAddedPo(poExternalId)
      } else {
        // Has connections — show confirmation
        setPoToRemove({
          externalId: poExternalId,
          connectionCount: lineItemMatches.length,
        })
        removePOOverlay.open()
      }
    },
    [poLineItemMatchPerPoId, removeUserAddedPo, removePOOverlay],
  )

  const handleConfirmRemovePO = useCallback(() => {
    if (!poToRemove) return
    // Delete all line item matches for this PO
    const matches = poLineItemMatchPerPoId.get(poToRemove.externalId) ?? []
    deletePOLineItemMatches(matches.map((m) => m.id))
    removeUserAddedPo(poToRemove.externalId)
    setPoToRemove(null)
    removePOOverlay.close()
  }, [
    deletePOLineItemMatches,
    poLineItemMatchPerPoId,
    poToRemove,
    removeUserAddedPo,
    removePOOverlay,
  ])

  const {
    sharedInvoiceJobNumber,
    sharedInvoicePhaseNumber,
    showHeaderJobNumber,
    showHeaderPhaseNumber,
  } = useMemo(() => {
    const sharedInvoiceJobNumber = getSharedLineItemJob(lineItems)
    const sharedInvoicePhaseNumber = getSharedLineItemPhase(lineItems)

    let showHeaderJobNumber = sharedInvoiceJobNumber !== undefined
    let showHeaderPhaseNumber = sharedInvoicePhaseNumber !== undefined

    for (const po of pdPurchaseOrders) {
      if (!showHeaderJobNumber && !showHeaderPhaseNumber) break

      const poLineItems = po.line_items.map((li) => ({
        jobNumber: li.job_num,
        phaseNumber: li.phase_num,
      }))

      if (
        showHeaderJobNumber &&
        getSharedLineItemJob(poLineItems) === undefined
      ) {
        showHeaderJobNumber = false
      }

      if (
        showHeaderPhaseNumber &&
        getSharedLineItemPhase(poLineItems) === undefined
      ) {
        showHeaderPhaseNumber = false
      }
    }

    return {
      sharedInvoiceJobNumber,
      sharedInvoicePhaseNumber,
      showHeaderJobNumber,
      showHeaderPhaseNumber,
    }
  }, [lineItems, pdPurchaseOrders])

  const displayPurchaseOrders = useInitialPOSort(
    sortedPurchaseOrders,
    lineItems,
    poLineItemMatchPerPoId,
    isLoadingPOs,
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.default',
        backgroundImage:
          'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
    >
      {/* Main content: side-by-side layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Invoice panel (left) */}
        <Box
          ref={invoiceScrollRef}
          sx={{
            width: '45%',
            overflow: 'auto',
            overscrollBehavior: 'none',
            p: 2,
          }}
        >
          <DataGroupNode
            title="Invoice"
            icon={ReceiptLongIcon}
            amountTotal={amountTotal}
            poNumber={purchaseOrderNumber}
          >
            <Stack
              direction="row"
              spacing={3}
              sx={{ mb: 2 }}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              <FieldItem
                label="Vendor"
                value={vendorName}
                action={
                  suggestedVendor && vendorField && (
                    <Tooltip title="Update Vendor">
                      <IconButton
                        aria-label="Update vendor"
                        color="primary"
                        size="small"
                        onClick={vendorUpdateOverlay.open}
                      >
                        <CompareArrows fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              />
              <FieldItem label="Invoice Date" value={invoiceDate} />
              <FieldItem label="Due Date" value={dueDate} />
              {showHeaderJobNumber && (
                <FieldItem
                  label="Job"
                  value={sharedInvoiceJobNumber || null}
                  tooltip={lineItems[0]?.jobDisplayName}
                />
              )}
              {showHeaderPhaseNumber && (
                <FieldItem
                  label="Phase"
                  value={sharedInvoicePhaseNumber || null}
                  tooltip={lineItems[0]?.phaseDisplayName}
                />
              )}
            </Stack>

            <Divider />

            <LineItemsTable
              rows={lineItems}
              rowRefCallback={invoiceRowRefCallback}
              highlightedRowIds={highlightedInvoiceRowIds}
              showJobColumn={!showHeaderJobNumber}
              showPhaseColumn={!showHeaderPhaseNumber}
            />
          </DataGroupNode>
        </Box>

        {/* Connector column (middle) */}
        <Box
          ref={connectorRef}
          sx={{
            width: '10%',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <ConnectorCanvas
            matches={allMatchLines}
            invoiceRowPositions={invoiceRowPositions}
            poRowPositions={poRowPositions}
            connectedInvoiceRowIds={connectedInvoiceRowIds}
            connectedPoRowIds={connectedPoRowIds}
            poLineItemMatchesQueryKey={poLineItemMatchesQueryKey}
            onConnect={handleDragConnect}
            onSelectionChange={handleConnectorSelectionChange}
          />
        </Box>

        {/* Purchase Orders panel (right) */}
        <Box
          ref={poScrollRef}
          sx={{
            width: '45%',
            overflow: 'auto',
            overscrollBehavior: 'none',
            p: 2,
          }}
        >
          {isLoadingPOs ? (
            <SkeletonPOCard />
          ) : pdPurchaseOrders.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                textAlign: 'center',
                gap: 1.5,
              }}
            >
              <ShoppingCartIcon
                sx={{ fontSize: 56, color: 'text.disabled', mb: 0.5 }}
              />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                No purchase order matches
              </Typography>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ maxWidth: 280 }}
              >
                No purchase orders have been matched to this invoice yet. Add
                one to get started.
              </Typography>
              <Button
                startIcon={<Add />}
                color="green"
                onClick={searchPurchaseOrderOverlay.open}
                sx={{ mt: 1 }}
              >
                Add Purchase Order
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {displayPurchaseOrders.map((po) => {
                const hasConnections = po.line_items.some((li) =>
                  connectedPoRowIds.has(`${po.external_id}|${li.external_id}`),
                )

                return (
                  <DataGroupNode
                    key={po.external_id}
                    title="Purchase Order"
                    icon={ShoppingCartIcon}
                    poNumber={po.number}
                    headerColor={hasConnections ? green[500] : 'grey.500'}
                    onClose={() => handleRemovePO(po.external_id)}
                    amountTotal={po.amount}
                  >
                    <PurchaseOrderData
                      vendorName={
                        po.vendor_id
                          ? vendorMap.get(po.vendor_id)?.pd_display_name || null
                          : null
                      }
                      showHeaderJobNumber={showHeaderJobNumber}
                      showHeaderPhaseNumber={showHeaderPhaseNumber}
                      date={po.posted_on}
                      lineItems={po.line_items.map((li) => ({
                        rowId: `${po.external_id}|${li.external_id}`,
                        description: li.description,
                        jobNumber: li.job_num || null,
                        phaseNumber: li.phase_num || null,
                        quantity: li.quantity,
                        remainingQuantity: li.remaining_quantity,
                        rate: li.unit_price,
                        amount: li.amount,
                        remainingAmount: li.remaining_amount,
                      }))}
                      rowRefCallback={poRowRefCallback}
                      highlightedRowIds={highlightedPoRowIds}
                    />
                  </DataGroupNode>
                )
              })}
            </Stack>
          )}
          {pdPurchaseOrders.length > 0 && (
            <Button
              color="green"
              variant="text"
              startIcon={<Add />}
              onClick={searchPurchaseOrderOverlay.open}
              sx={{ mt: 2, ml: 'auto', display: 'flex' }}
            >
              Add Purchase Order
            </Button>
          )}
        </Box>
      </Box>

      <CreateMatchConfirmDialog
        overlay={createLineItemMatchOverlay}
        doNotShowAgain={doNotShowAgain}
        isCreating={isCreatingLineItemMatch}
        onClose={handleCreateConfirmClose}
        onDoNotShowAgainChange={setDoNotShowAgain}
        onConfirm={handleConfirmCreate}
      />

      <AddPurchaseOrderDialog
        overlay={searchPurchaseOrderOverlay}
        orgId={doc.owner_org_id}
        orgCredentialTypeId={project.org_credential_type_id || ''}
        defaultVendorId={vendorId}
        addedPoIds={poIds}
        onAddPurchaseOrder={handleAddPurchaseOrder}
      />
      {suggestedVendor && (
        <UpdateVendorPopover
          overlay={vendorUpdateOverlay}
          suggestedVendor={suggestedVendor}
          vendorData={{
            value: vendorName,
            field: vendorField as ProjectGridField,
          }}
        />
      )}

      <Dialog
        {...removePOOverlay}
        title="Remove Purchase Order"
        onClose={() => {
          setPoToRemove(null)
          removePOOverlay.close()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>
            This purchase order has{' '}
            <strong>
              {poToRemove?.connectionCount} line item{' '}
              {poToRemove?.connectionCount === 1 ? 'match' : 'matches'}
            </strong>
            . Removing it will delete all connections to this purchase order.
          </Typography>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="text"
            onClick={() => {
              setPoToRemove(null)
              removePOOverlay.close()
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemovePO}
          >
            Remove
          </Button>
        </DialogFooter>
      </Dialog>
    </Box>
  )
}
